var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { deactivateAddress, deactivatePrimaryAddress, getAllAddresses, saveAddress, } from 'actions/Address';
import { AddressItem, EditAddressForm, NewMainAddress } from 'address';
import { removeRecipientIfPresent } from 'checkout';
import { Button, ButtonRow, Card, CheckboxWithLabel, LinkBox, ListPageHeader, Modal } from 'common';
import Loading from 'components/UI/LoadingScreen';
import Page from 'components/UI/Page';
import { GenericAddressForm } from 'delivery';
import { sortBy } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AddressUploadResultState } from 'types/address';
import './AddressPage.scss';
import { generalError } from 'utilities/error';
import axios from 'axios';
function NewAddressForm({ addresses, primary, setPrimary, setShowNewAddressForm, setShouldDeleteCurrentPrimary, shouldDeleteCurrentPrimary, }) {
    const dispatch = useDispatch();
    const [previousAddresses, setPreviousAddresses] = useState([]);
    useEffect(() => {
        window.scrollTo({ top: 0 });
        // If there is a change in addresses, the saving is done and NewAddressForm can be closed
        if (previousAddresses.length !== 0) {
            setShowNewAddressForm(false);
        }
        else {
            setPreviousAddresses(addresses);
        }
    }, [addresses]);
    const savingAddress = useSelector((state) => state.address.savingAddress);
    const error = useSelector((state) => state.address.savingAddressError);
    const b2bMode = useSelector((state) => state.ui.isB2BModeEnabled);
    const [addressCompleteHandler, setAddressCompleteHandler] = useState(undefined);
    const handleAddressComplete = () => {
        if (!savingAddress && addressCompleteHandler) {
            addressCompleteHandler.handleAddressComplete();
        }
    };
    const handleSaveAddress = useCallback((address) => {
        if (address) {
            dispatch(saveAddress(address.deliveryAddress, primary, true, b2bMode));
            if (shouldDeleteCurrentPrimary) {
                dispatch(deactivatePrimaryAddress());
                setShouldDeleteCurrentPrimary(false);
            }
        }
    }, [primary]);
    return (React.createElement(Page, { className: "address-page--new-address", title: "Ny adresse" }, savingAddress
        ? (React.createElement(Loading, null))
        : (React.createElement("div", { className: "address-page--new-address__form" },
            error && React.createElement("div", { className: "address-page--new-address__form__error" }, generalError(error)),
            !b2bMode && React.createElement("div", { className: "address-page--new-address__form__checkbox-container" },
                React.createElement(CheckboxWithLabel, { isChecked: primary, setIsChecked: (on) => setPrimary(on), labelText: "Bruk som hovedadresse" })),
            React.createElement(GenericAddressForm, { onComplete: handleSaveAddress, hideButton: true, setAddressCompleteHandler: setAddressCompleteHandler }),
            React.createElement(ButtonRow, { display: "space-between" },
                React.createElement(Button, { type: "action", style: "ghost", onClick: () => setShowNewAddressForm(false) }, "Tilbake"),
                React.createElement(Button, { type: "action", style: "primary", onClick: handleAddressComplete }, "Legg til"))))));
}
const addressUploadResultStateLabels = {
    [AddressUploadResultState.ADDRESS_ADDED]: 'Adresse lagt til',
    [AddressUploadResultState.ADDRESS_ALREADY_IN_DATABASE]: 'Adresse lagt til',
    [AddressUploadResultState.ERROR]: 'Opplastingsfeil',
};
const addressUploadWellKnownErrorMessagesLabels = {
    ADDRESS_NO_CARRIER_SUPPORT: 'Vi leverer dessverre ikke til denne adressen',
    ADDRESS_NO_REGION_ID_DISTRIBUTION_CHECK: 'No regionId in DistributionCheck result',
    ADDRESS_REGION_MISSING: 'No region with given ID in the database',
    // Add any other known error codes here...
};
export function getHumanReadableMessage(message) {
    if (!message) {
        return '';
    }
    return addressUploadWellKnownErrorMessagesLabels[message] || message;
}
export function BatchUploadAddressForm({ setShowB2BBatchUploadAddressForm, }) {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const currentCompanyName = useSelector((state) => { var _a; return (_a = state.b2bCompany.currentCompany) === null || _a === void 0 ? void 0 : _a.companyName; });
    const [results, setResults] = useState(null);
    const excelFileInputRef = useRef(null);
    const handleFileButtonClick = () => {
        var _a;
        (_a = excelFileInputRef.current) === null || _a === void 0 ? void 0 : _a.click();
    };
    const handleDownloadExampleExcelFileClick = () => __awaiter(this, void 0, void 0, function* () {
        const exampleExcelFileName = 'Adressemal-Morgenlevering.xlsx';
        const url = `/static/` + exampleExcelFileName;
        const response = yield fetch(url, {});
        const blobFromResponse = yield response.blob();
        if (!response.ok) {
            throw new Error(`Server returned ${response.status} ${response.statusText} when fetching example Excel file`);
        }
        const blobUrl = URL.createObjectURL(blobFromResponse);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = exampleExcelFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    });
    const handleUpload = (file) => __awaiter(this, void 0, void 0, function* () {
        setLoading(true);
        setError(null);
        try {
            const formData = new FormData();
            // 'file' must match the name expected by the server
            formData.append('file', file);
            const response = yield axios.post(`${process.env.CORE_API}/address/company/addresses`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            // Assuming the server returns an array of AddressUploadResult objects
            setResults(response.data);
            const newAddressAdded = response.data.find(a => a.addressUploadResultState == AddressUploadResultState.ADDRESS_ADDED);
            if (newAddressAdded) {
                dispatch(getAllAddresses(true));
            }
        }
        catch (err) {
            // Try to extract an error message from the response
            const errMsg = err.response && err.response.data && err.response.data.message
                ? "Kunne ikke laste opp adresser: " + err.response.data.message
                : 'Opplasting mislyktes pga. serverfeil.';
            setError(errMsg);
        }
        finally {
            setLoading(false);
        }
    });
    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleUpload(e.target.files[0]);
        }
        else {
        }
    };
    return (React.createElement(Page, { className: "address-page--batch-upload-address", title: "Batch upload address for company" }, loading
        ? (React.createElement("div", null,
            React.createElement(Loading, null),
            React.createElement("div", { style: { textAlign: 'center', fontWeight: 'bold' } }, "Uploading addresses, this may take couple of minutes.")))
        : (React.createElement("div", { className: "address-page--batch-upload-address__form" },
            error && React.createElement("div", { className: "address-page--batch-upload-address__form__error" }, generalError(error)),
            React.createElement("div", null,
                React.createElement(Card, { title: `Last opp adresser for ${currentCompanyName}` },
                    React.createElement(ButtonRow, { display: "space-between" },
                        React.createElement(Button, { type: "action", style: "primary", onClick: handleFileButtonClick, disabled: loading }, loading ? 'Laster opp...' : 'Last opp Excelfil'),
                        React.createElement(Button, { type: "action", style: "ghost", onClick: handleDownloadExampleExcelFileClick, disabled: loading }, "Last ned mal"),
                        React.createElement("form", null,
                            React.createElement("input", { type: "file", id: "fileUpload", accept: ".xlsx", style: { display: 'none' }, onChange: handleFileChange, ref: excelFileInputRef }))))),
            error && (React.createElement("div", { style: { color: 'red', marginTop: '1rem' } },
                "Error: ",
                error)),
            results && (React.createElement("div", { style: { marginTop: '1rem', marginBottom: '1em' } },
                React.createElement("h3", null, "Upload Results"),
                React.createElement("table", { style: { width: '100%', borderCollapse: 'collapse' } },
                    React.createElement("thead", null,
                        React.createElement("tr", null,
                            React.createElement("th", { style: { border: '1px solid #ccc', padding: '0.5rem' } }, "Address"),
                            React.createElement("th", { style: { border: '1px solid #ccc', padding: '0.5rem' } }, "Success"),
                            React.createElement("th", { style: { border: '1px solid #ccc', padding: '0.5rem' } }, "State"),
                            React.createElement("th", { style: { border: '1px solid #ccc', padding: '0.5rem' } }, "Message"))),
                    React.createElement("tbody", null, results.map((result, index) => (React.createElement("tr", { key: index },
                        React.createElement("th", { style: { border: '1px solid #ccc', padding: '0.5rem' } }, result.address),
                        React.createElement("td", { style: { border: '1px solid #ccc', padding: '0.5rem' } }, result.addressUploadResultState == AddressUploadResultState.ERROR ? '❌' : '✅'),
                        React.createElement("td", { style: { border: '1px solid #ccc', padding: '0.5rem' } }, addressUploadResultStateLabels[result.addressUploadResultState]),
                        React.createElement("td", { style: { border: '1px solid #ccc', padding: '0.5rem' } }, getHumanReadableMessage(result.message))))))))),
            React.createElement(ButtonRow, { display: "space-between" },
                React.createElement(Button, { type: "action", style: "ghost", onClick: () => setShowB2BBatchUploadAddressForm(false) }, "Tilbake"))))));
}
function PrimaryAddress({ addresses, canDeliver, primaryAddress, setShowNewAddressForm, setPrimary, setEditAddress, setShouldDeleteCurrentPrimary, shouldDeleteCurrentPrimary, }) {
    const [newMainAddressModalOpen, setNewMainAddressModalOpen] = useState(false);
    const openNewPrimaryAddress = () => {
        setShowNewAddressForm(true);
        setPrimary(true);
    };
    const deletePrimaryAddress = () => {
        setShouldDeleteCurrentPrimary(true);
        setNewMainAddressModalOpen(true);
    };
    const setNewPrimaryAddress = () => {
        if (isEmpty(primaryAddress) && isEmpty(addresses)) {
            openNewPrimaryAddress();
        }
        else {
            setNewMainAddressModalOpen(true);
        }
    };
    return (React.createElement("div", { className: "address-page__primary" },
        React.createElement("h2", null, "Hovedadresse"),
        React.createElement("p", null, "Hovedadressen brukes til fast levering og hvis en annen adresse ikke er valgt."),
        newMainAddressModalOpen && (React.createElement(Modal, { className: "address-page__primary__modal", labelId: "edit-address-form-title", onClose: () => {
                setShouldDeleteCurrentPrimary(false);
                setNewMainAddressModalOpen(false);
            } },
            React.createElement(NewMainAddress, { addresses: addresses, closeModal: () => setNewMainAddressModalOpen(false), openNewPrimaryAddress: openNewPrimaryAddress, shouldDeleteCurrentPrimary: shouldDeleteCurrentPrimary }))),
        React.createElement("div", null,
            React.createElement("div", { className: "address-page__list" }, !isEmpty(primaryAddress) && (React.createElement(AddressItem, { address: primaryAddress.deliveryAddress, isPrimary: true, onEdit: () => setEditAddress(primaryAddress), onDelete: deletePrimaryAddress, warningText: !canDeliver ? 'Denne adressen er ikke i valgt region' : undefined })))),
        React.createElement("div", { className: "address-page__buttons" },
            React.createElement(LinkBox, { colorStyle: "gray", text: !isEmpty(primaryAddress) ? 'Endre hovedadresse' : 'Ny hovedadresse', onClick: setNewPrimaryAddress }))));
}
function AddressList({ addresses, primaryAddress, setShowNewAddressForm, setPrimary, setShouldDeleteCurrentPrimary, shouldDeleteCurrentPrimary, setShowB2BBatchUploadAddressForm, }) {
    const dispatch = useDispatch();
    const [editAddress, setEditAddress] = useState();
    const [focusPhoneInput, setFocusPhoneInput] = useState(false);
    const loading = useSelector((state) => state.address.loading);
    const isB2BModeEnabled = useSelector((state) => state.ui.isB2BModeEnabled);
    const canDeliver = useSelector((state) => state.distribution.canDeliver || state.distribution.distributionLoading);
    const handleEditModalClose = () => {
        setEditAddress(undefined);
    };
    const openNewPrimaryAddress = () => {
        setShowNewAddressForm(true);
        setPrimary(true);
        setShowB2BBatchUploadAddressForm(false);
    };
    return (React.createElement("div", { className: "address-page" },
        React.createElement(React.Fragment, null,
            editAddress && (React.createElement(EditAddressForm, { addressToEdit: editAddress, closeModal: handleEditModalClose, openNewPrimaryAddress: openNewPrimaryAddress, focusPhoneInput: focusPhoneInput })),
            React.createElement("div", null,
                React.createElement(ListPageHeader, null, "Adresser"),
                loading && React.createElement(Loading, null),
                !loading && (React.createElement("div", { className: "address-page__buttons" },
                    React.createElement(LinkBox, { colorStyle: "gray", text: "Ny adresse", onClick: () => {
                            setShowNewAddressForm(true);
                            setPrimary(false);
                            setShowB2BBatchUploadAddressForm(false);
                        } }))),
                !loading && isB2BModeEnabled && (React.createElement("div", { className: "address-page__buttons", 
                    // Adding margin to the top so that "Ny adresse" and this button are at least slightly separated in the UI
                    style: { marginTop: '1rem' } },
                    React.createElement(LinkBox, { colorStyle: "gray", text: "Last opp adresser", onClick: () => {
                            setShowNewAddressForm(false);
                            setPrimary(false);
                            setShowB2BBatchUploadAddressForm(true);
                        } })))),
            !loading && (React.createElement(React.Fragment, null,
                !isB2BModeEnabled && (React.createElement(PrimaryAddress, { addresses: addresses, canDeliver: canDeliver, primaryAddress: primaryAddress, setEditAddress: setEditAddress, setShowNewAddressForm: setShowNewAddressForm, setPrimary: setPrimary, setShouldDeleteCurrentPrimary: setShouldDeleteCurrentPrimary, shouldDeleteCurrentPrimary: shouldDeleteCurrentPrimary, setShowB2BBatchUploadAddressForm: setShowB2BBatchUploadAddressForm })),
                React.createElement("div", null,
                    React.createElement("h2", null, "Alle adresser"),
                    !addresses.length && React.createElement("p", null, "Adresser som har blitt brukt i bestillinger vil vises her."),
                    React.createElement("div", { className: "address-page__list" }, sortBy(addresses, (a) => { var _a; return (_a = a.deliveryAddress.firstName) === null || _a === void 0 ? void 0 : _a.toLowerCase(); }).map((address) => {
                        const { addressId, deliveryAddress, isPrimary } = address;
                        if (isPrimary)
                            return;
                        return (React.createElement(AddressItem, { key: addressId, address: deliveryAddress, onDelete: () => {
                                dispatch(deactivateAddress(deliveryAddress, addressId, isB2BModeEnabled));
                                dispatch(removeRecipientIfPresent(deliveryAddress));
                            }, onEdit: (focusPhone) => {
                                setFocusPhoneInput(focusPhone || false);
                                setEditAddress(address);
                            }, warningText: !canDeliver
                                ? 'Denne adressen er ikke i valgt region'
                                : undefined }));
                    }))))))));
}
function AddressPage() {
    const dispatch = useDispatch();
    const [showNewAddressForm, setShowNewAddressForm] = useState(false);
    const [showB2BBatchUploadAddressForm, setShowB2BBatchUploadAddressForm] = useState(false);
    const [primary, setPrimary] = useState(false);
    const [shouldDeleteCurrentPrimary, setShouldDeleteCurrentPrimary] = useState(false);
    const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
    const isB2BModeEnabled = useSelector((state) => state.ui.isB2BModeEnabled);
    const addresses = useSelector((state) => state.address.allAddresses);
    const primaryAddress = addresses.filter((a) => a.isPrimary)[0];
    useEffect(() => {
        isLoggedIn && dispatch(getAllAddresses(isB2BModeEnabled));
    }, [isLoggedIn, isB2BModeEnabled]);
    return showNewAddressForm ? (React.createElement(NewAddressForm, { addresses: addresses, primary: primary, setPrimary: setPrimary, setShowNewAddressForm: setShowNewAddressForm, setShouldDeleteCurrentPrimary: setShouldDeleteCurrentPrimary, shouldDeleteCurrentPrimary: shouldDeleteCurrentPrimary, setShowB2BBatchUploadAddressForm: setShowB2BBatchUploadAddressForm })) : showB2BBatchUploadAddressForm ? (React.createElement(BatchUploadAddressForm, { setShowB2BBatchUploadAddressForm: setShowB2BBatchUploadAddressForm })) : (React.createElement(AddressList, { addresses: addresses, primaryAddress: primaryAddress, setPrimary: setPrimary, setShowNewAddressForm: setShowNewAddressForm, setShouldDeleteCurrentPrimary: setShouldDeleteCurrentPrimary, shouldDeleteCurrentPrimary: shouldDeleteCurrentPrimary, setShowB2BBatchUploadAddressForm: setShowB2BBatchUploadAddressForm }));
}
export default AddressPage;
